import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Card, Empty, Loading, Plate, Stat, StatusBadge, TypeBadge, formatDateTime, formatKm } from '../components/ui';

interface Dashboard {
  kacheln: Record<string, number>;
  schadensarten: { kind: string; label: string; anzahl: number }[];
  typVerteilung: { type: string; label: string; anzahl: number }[];
  letzteProtokolle: any[];
  checkFaellig: { id: string; plate: string; make: string | null; model: string | null; usage: string }[];
}

export const DashboardPage: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<Dashboard>('/statistik/dashboard'),
  });
  const { data: verlauf } = useQuery({
    queryKey: ['verlauf'],
    queryFn: () => api<{ datum: string; anzahl: number }[]>('/statistik/verlauf?tage=30'),
  });

  if (isLoading || !data) return <Loading />;
  const k = data.kacheln;
  const max = Math.max(1, ...(verlauf ?? []).map((v) => v.anzahl));

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="grid k4">
        <Stat label="Fahrzeuge" value={k.fahrzeugeGesamt} hint={`davon ${k.poolGesamt} Poolfahrzeuge`} />
        <Stat
          label="Pool unterwegs"
          value={k.poolUnterwegs}
          hint={`${k.offeneFahrten} offene Fahrt(en)`}
          tone="accent"
        />
        <Stat label="Protokolle (30 Tage)" value={k.protokolle30} hint={`${k.protokolleGesamt} insgesamt`} />
        <Stat
          label="Check-In fällig"
          value={k.checkFaelligAnzahl}
          hint={`${k.checksLetzte7} Prüfungen in 7 Tagen`}
          tone={k.checkFaelligAnzahl > 0 ? 'warn-accent' : 'ok-accent'}
        />
      </div>

      {k.versandfehler > 0 || k.entwuerfe > 0 ? (
        <div className="alert info">
          {k.versandfehler > 0 ? <b>{k.versandfehler} Protokoll(e) mit Versandfehler. </b> : null}
          {k.entwuerfe > 0 ? <>{k.entwuerfe} Entwurf/Entwürfe noch nicht abgeschlossen.</> : null}
        </div>
      ) : null}

      <div className="grid k2">
        <Card title="Protokolle je Tag (30 Tage)">
          <div className="bars">
            {(verlauf ?? []).map((v) => (
              <div
                key={v.datum}
                className={`bar ${v.anzahl >= max && max > 0 ? 'hot' : ''}`}
                style={{ height: `${(v.anzahl / max) * 100}%` }}
                title={`${v.datum}: ${v.anzahl}`}
              />
            ))}
          </div>
          <div className="row small muted" style={{ justifyContent: 'space-between', marginTop: 8 }}>
            <span>vor 30 Tagen</span>
            <span>heute</span>
          </div>
        </Card>

        <Card title="Erfasste Schäden nach Art">
          {data.schadensarten.length === 0 ? (
            <Empty text="Noch keine Schäden erfasst" />
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {data.schadensarten.map((s) => {
                const total = data.schadensarten.reduce((a, b) => a + b.anzahl, 0);
                return (
                  <div key={s.kind}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span>{s.label}</span>
                      <b>{s.anzahl}</b>
                    </div>
                    <div className="progress">
                      <span style={{ width: `${(s.anzahl / total) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Card
        title="Zuletzt erfasste Vorgänge"
        actions={<Link className="btn sm" to="/protokolle">Alle anzeigen</Link>}
        bodyless
      >
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Beleg-Nr.</th>
                <th>Vorgang</th>
                <th>Fahrzeug</th>
                <th>Zeitpunkt</th>
                <th className="num">km</th>
                <th className="num">Schäden</th>
                <th className="num">Fotos</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.letzteProtokolle.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link className="mono" to={`/protokolle/${p.id}`}>{p.number}</Link>
                  </td>
                  <td><TypeBadge type={p.type} /></td>
                  <td>
                    <Plate value={p.vehicle.plate} />{' '}
                    <span className="muted small">{[p.vehicle.make, p.vehicle.model].filter(Boolean).join(' ')}</span>
                  </td>
                  <td>{formatDateTime(p.occurredAt)}</td>
                  <td className="num">{formatKm(p.mileage)}</td>
                  <td className="num">{p._count.damages}</td>
                  <td className="num">{p._count.photos}</td>
                  <td><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {data.checkFaellig.length > 0 ? (
        <Card title={`Zustandsprüfung fällig (${data.checkFaellig.length} Fahrzeuge ohne Check-In in 7 Tagen)`}>
          <div className="chip-list">
            {data.checkFaellig.map((v) => (
              <Link key={v.id} to={`/fahrzeuge/${v.id}`} className="badge warn" style={{ padding: '5px 10px' }}>
                {v.plate} · {[v.make, v.model].filter(Boolean).join(' ')}
              </Link>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
};
